import React from "react";
import { Link } from "react-router-dom";

import type { OkkStatus } from "@/api/receiving";
import { useAuth } from "@/auth/useAuth";
import { statusInfo } from "@/lib/okk-receiving";
import { cn } from "@/lib/utils";
import { usePortalConfig } from "@/portal/usePortalConfig";

const TONE = {
  ok: "bg-emerald-100 text-emerald-800",
  warn: "bg-amber-100 text-amber-900",
  muted: "bg-slate-200 text-slate-700",
  bad: "bg-rose-100 text-rose-800",
} as const;

/**
 * Статус проверки ОКК у прихода. Виден всем, кто видит приход (склад
 * спрашивает «когда можно в работу?»), ссылкой на проверку — только при
 * праве `okk_receiving` и включённом разделе: иначе ссылка вела бы в 403.
 */
export function OkkBadge({
  okk,
  className,
}: {
  okk: OkkStatus | null;
  className?: string;
}): React.ReactElement | null {
  const { user } = useAuth();
  const { hasPage } = usePortalConfig();
  if (!okk) return null;
  const st = statusInfo(okk.status);
  const canOpen =
    hasPage("okk_receiving") &&
    (user?.is_admin === true || user?.permissions.includes("okk_receiving") === true);
  const cls = cn("inline-block rounded px-1.5 py-0.5 text-[10px] font-medium", TONE[st.tone], className);
  const text = `ОКК: ${st.text}`;
  if (!canOpen) {
    return (
      <span className={cls} data-testid="okk-badge">
        {text}
      </span>
    );
  }
  return (
    <Link
      to={`/okk-receiving/${okk.inspection_id}`}
      className={cn(cls, "underline-offset-2 hover:underline")}
      data-testid="okk-badge"
    >
      {text} →
    </Link>
  );
}
